import { Injectable } from '@angular/core';
import RecordRTC from 'recordrtc';
import { Observable, Subject } from 'rxjs';
import { ChatLoggingService } from './chat-logging.service';
import { ChatCloudLinkService } from './chat-cloudlink.service';

declare var MediaRecorder: any;

export class RecordingSpectrum {
  spectrum: Uint8Array<ArrayBuffer>;
}

@Injectable({
  providedIn: 'root'
})
export class RecordService {
  private stream: MediaStream;
  private clonedStream: MediaStream;
  private recordRTC: any;
  private mediaOptions: MediaOptions;

  public isPermissionDenied: boolean = false;
  public isDeviceNotFound: boolean = false;
  public isDeviceBusy: boolean = false;

  private audioContext: AudioContext;
  private silentGain: GainNode;
  private animationFrameId: number | null = null;
  private analyser: AnalyserNode;
  private input: MediaStreamAudioSourceNode;
  private spectrum: Uint8Array<ArrayBuffer>;
  public audioProcess$: Subject<RecordingSpectrum> = new Subject<RecordingSpectrum>();
  public currentlyPlaying$: Subject<string> = new Subject<string>();

  constructor(
    private chatLoggingService: ChatLoggingService,
    private chatCloudLinkService: ChatCloudLinkService
  ) {}

  // RecordRTC writes straight to the console, so only let it through when debug logging to the console is on
  private get disableRecordRTCLogs(): boolean {
    return !(this.chatLoggingService.consoleOutput && this.chatLoggingService.isLevelEnabled('debug'));
  }

  startVideoPreview(mediaStreamConstraints: MediaStreamConstraints, videoElement: HTMLVideoElement): Promise<void> {
    return navigator.mediaDevices
      .getUserMedia(mediaStreamConstraints)
      .then((mediaStream: MediaStream) => {
        this.classifyMediaError(null);
        videoElement.volume = 0;
        videoElement.srcObject = mediaStream;
      })
      .catch(error => {
        this.classifyMediaError(error);
        this.chatLoggingService.error({
          logMessage: `record.service.startVideoPreview() ${error instanceof DOMException ? error.name : ''}`,
          logParameters: [error]
        });
        throw error;
      });
  }

  stopVideoPreview(videoElement: HTMLVideoElement): void {
    for (const stream of (videoElement.srcObject as MediaStream).getTracks()) {
      stream.stop();
    }
  }

  startVideoRecording(videoElement: HTMLVideoElement): void {
    try {
      if (!videoElement.srcObject) {
        throw new DOMException('No active video preview stream to record from.', 'NotFoundError');
      }
      this.recordRTC = RecordRTC(videoElement.srcObject as MediaStream, {
        type: 'video',
        mimeType: 'video/webm; codecs="opus,vp8"',
        recorderType: RecordRTC.MediaStreamRecorder,
        disableLogs: this.disableRecordRTCLogs
      });
      this.recordRTC.startRecording();
      this.classifyMediaError(null);
    } catch (error) {
      this.classifyMediaError(error);
      this.chatLoggingService.error({
        logMessage: `record.service.startVideoRecording() ${error instanceof DOMException ? error.name : ''}`,
        logParameters: [error]
      });
      throw error;
    }
  }

  async stopVideoRecording(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      if (!this.recordRTC) {
        reject('RecordRTC is undefined, assuming nothing is recording');
      } else {
        this.recordRTC.stopRecording(() => {
          resolve(this.recordRTC.getBlob());
        });
      }
    });
  }

  startRecording(mediaStreamConstraints: MediaStreamConstraints, mediaOptions?: MediaOptions): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      if (!mediaOptions) {
        this.mediaOptions = {
          type: 'audio',
          recorderType: RecordRTC.StereoAudioRecorder,
          numberOfAudioChannels: 1
        };
        if (this.chatCloudLinkService.browser.name.toLowerCase() === 'safari') {
          this.mediaOptions = {
            mimeType: 'audio/ogg',
            numberOfAudioChannels: 1,
            bitsPerSecond: 96000
          };
          if (typeof MediaRecorder === 'undefined') {
            this.mediaOptions.recorderType = RecordRTC.StereoAudioRecorder;
          }
        }
      }

      // TODO Trying to use the RecordRTC AudioContext doesn't appear to work on iPhone 6 safari with iOS 12, but want to revisit
      // if (RecordRTC && RecordRTC.Storage && RecordRTC.Storage.AudioContextConstructor) {
      //     this.audioContext = RecordRTC.Storage.AudioContextConstructor;
      // } else {
      //     RecordRTC.Storage.AudioContextConstructor = new RecordRTC.Storage.AudioContext();
      //     this.audioContext = RecordRTC.Storage.AudioContextConstructor;
      // }

      var AudioContext =
        (<any>window).AudioContext || // Default
        (<any>window).webkitAudioContext || // Safari and old versions of Chrome
        false;

      if (AudioContext) {
        this.chatLoggingService.info({
          logMessage: `record.service.startRecording() RTC got AudioContext`
        });
        this.audioContext = new AudioContext();
        this.silentGain = this.audioContext.createGain();
        this.silentGain.gain.value = 0;
        this.silentGain.connect(this.audioContext.destination);
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.smoothingTimeConstant = 0.2;
        this.analyser.fftSize = 1024;
        this.spectrum = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount));
      }

      navigator.mediaDevices.getUserMedia(mediaStreamConstraints).then(
        stream => {
          if (this.audioContext) {
            this.input = this.audioContext.createMediaStreamSource(stream);
            this.input.connect(this.analyser);
            this.analyser.connect(this.silentGain);
            // Figured most of this out via https://hackernoon.com/creative-coding-using-the-microphone-to-make-sound-reactive-art-part1-164fd3d972f3
            const poll = () => {
              if (!this.analyser) return;
              try {
                this.analyser.getByteTimeDomainData(this.spectrum);
                this.audioProcess$.next({ spectrum: this.spectrum });
              } catch {}
              this.animationFrameId = requestAnimationFrame(poll);
            };
            this.animationFrameId = requestAnimationFrame(poll);
          }

          //If you pass a stream into two different functions in iOS Safari, it appears it only applies to the first function used
          //So I could only get ONE of audio process or record RTC working. Cloning solves this problem.
          if (
            this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
            this.chatCloudLinkService.browser.name.toLowerCase() === 'ios'
          ) {
            this.clonedStream = stream.clone();
            this.stream = stream;
            this.recordRTC = RecordRTC(this.clonedStream, {
              ...this.mediaOptions,
              disableLogs: this.disableRecordRTCLogs
            });
          } else {
            this.stream = stream;
            this.recordRTC = RecordRTC(this.stream, { ...this.mediaOptions, disableLogs: this.disableRecordRTCLogs });
          }

          this.chatLoggingService.info({
            logMessage: `record.service.startRecording() RTC start recording`
          });
          this.recordRTC.startRecording();

          this.classifyMediaError(null);
          resolve(null);
        },
        error => {
          this.classifyMediaError(error);
          if (error instanceof DOMException) {
            this.chatLoggingService.error({
              logMessage: `record.service.startRecording() DOMException ${error.code} ${error.message} ${error.name}`
            });
          } else {
            this.chatLoggingService.error({
              logMessage: `record.service.startRecording() error`,
              logParameters: [error]
            });
          }
          this.cleanUp();
          this.cleanUpStream();
          reject(error);
        }
      );
    });
  }

  // Commented out because we can't allow larger attachments (<= 10MB)
  // private createMediaOptions(): { mimeType: string, recorderType: any } {
  //     const mimeTypes = {
  //         mkv: 'video/x-matroska;codecs=avc1',
  //         webm_h264: 'video/webm\;codecs=h264',
  //         webm_vp9: 'video/webm\;codecs=vp9',
  //         webm_vp8: 'video/webm\;codecs=vp8',
  //         webm: 'video/webm'
  //     };

  //     const result = {
  //         type: 'video',
  //         mimeType: mimeTypes.mkv,
  //         recorderType: RecordRTC.MediaStreamRecorder
  //     };

  //     if (!MediaRecorder.isTypeSupported(result.mimeType)) {
  //         console.log('MKV not supported');
  //         result.mimeType = mimeTypes.webm_h264;

  //         if (!MediaRecorder.isTypeSupported(result.mimeType)) {
  //             console.log('webm/h264 not supported');
  //             result.mimeType = mimeTypes.webm_vp9;

  //             if (!MediaRecorder.isTypeSupported(result.mimeType)) {
  //                 console.log('webm/vp9 not supported');
  //                 result.mimeType = mimeTypes.webm_vp8;

  //                 if (!MediaRecorder.isTypeSupported(result.mimeType)) {
  //                     console.log('webm/vp8 not supported');
  //                     result.mimeType = mimeTypes.webm;
  //                     result.recorderType = RecordRTC.WhammyRecorder;
  //                 }
  //             }
  //         }
  //     }

  //     return result;
  // }

  stopRecording(): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      this.cleanUp();
      if (this.recordRTC) {
        this.recordRTC.stopRecording(() => {
          var blob = this.recordRTC.getBlob();
          resolve(blob);
        });
      }
      this.cleanUpStream();
    });
  }

  private cleanUpStream() {
    try {
      if (this.stream) {
        this.stream.getAudioTracks().forEach(track => track.stop());
        this.stream.getVideoTracks().forEach(track => track.stop());
        this.stream = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() stream.stop',
        logParameters: [ex]
      });
    }
    // TODO For some reason this code prevents audio animation from working on an iPhone 6 ios 12 Safari...
    // Does RecordRTC take care of it maybe? Investigate later, this might be a perf issue...
    // try {
    //     if(this.clonedStream) {
    //         this.clonedStream.getAudioTracks().forEach(track => track.stop());
    //         this.clonedStream.getVideoTracks().forEach(track => track.stop());
    //         this.clonedStream = null;
    //     }
    // } catch (ex) {
    //     this.chatLoggingService.error({
    //         logMessage: 'record.service.cleanUp() clonedStream.stop',
    //         logParameters: [ex]
    //     })
    // }
  }

  private cleanUp() {
    try {
      if (this.input) {
        this.input.disconnect();
        this.input = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() input.disconnect',
        logParameters: [ex]
      });
    }
    try {
      if (this.analyser) {
        this.analyser.disconnect();
        this.analyser = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() analyser.disconnect',
        logParameters: [ex]
      });
    }
    try {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() cancelAnimationFrame',
        logParameters: [ex]
      });
    }
    try {
      if (this.silentGain) {
        this.silentGain.disconnect();
        this.silentGain = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() silentGain.disconnect',
        logParameters: [ex]
      });
    }
    try {
      if (this.audioContext) {
        this.audioContext.close();
        this.audioContext = null;
      }
    } catch (ex) {
      this.chatLoggingService.error({
        logMessage: 'record.service.cleanUp() audioContext.close',
        logParameters: [ex]
      });
    }
  }

  download(fileName: string) {
    this.recordRTC.save(fileName);
  }

  // Classifies a getUserMedia()/RecordRTC failure so callers can show a cause-specific message.
  // Pass null to reset the flags after a successful start.
  private classifyMediaError(error: any): void {
    this.isPermissionDenied = false;
    this.isDeviceNotFound = false;
    this.isDeviceBusy = false;

    if (!(error instanceof DOMException)) {
      return;
    }

    switch (error.name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
      case 'SecurityError':
        this.isPermissionDenied = true;
        break;
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        this.isDeviceNotFound = true;
        break;
      case 'NotReadableError':
      case 'TrackStartError':
        this.isDeviceBusy = true;
        break;
    }
  }

  // A more accurate way to get overall volume
  private getRMS(spectrum) {
    var rms = 0;
    for (var i = 0; i < spectrum.length; i++) {
      rms += spectrum[i] * spectrum[i];
    }
    rms /= spectrum.length;
    rms = Math.sqrt(rms);
    return rms;
  }

  public mapSound(me, spectrum, total, _min?, _max?) {
    if (spectrum.length > 0) {
      // map to defaults if no values given
      var min = _min | 0;
      var max = _max | 100;
      //actual new freq
      var new_freq = Math.floor((me * spectrum.length) / total);
      // map the volumes to a useful number
      var mappedSound = this.map(Math.abs(spectrum[new_freq] - 128), 0, 128, min, max);
      //Safari iOS doesn't pick up sound as well apparently
      if (
        this.chatCloudLinkService.browser.os.toLowerCase() === 'ios' &&
        this.chatCloudLinkService.browser.name.toLowerCase() === 'ios'
      ) {
        return mappedSound * 10 > 128 ? 128 : mappedSound * 10;
      } else {
        return mappedSound;
      }
    } else {
      return 0;
    }
  }

  private map(value, min1, max1, min2, max2) {
    var returnvalue = ((value - min1) / (max1 - min1)) * (max2 - min2) + min2;
    return returnvalue;
  }
}

export interface MediaOptions {
  type?: 'video' | 'audio' | 'gif' | 'canvas';
  mimeType?: string;
  audioBitsPerSecond?: number;
  videoBitsPerSecond?: number;
  bitsPerSecond?: number;
  numberOfAudioChannels?: number;
  recorderType?: any;
}
